/**
 * Como a tela conta que um filtro é compartilhado com a equipe.
 *
 * ## Os dois campos que querem dizer a mesma coisa
 *
 * `is_default` e `is_public` tornam o filtro visível para todo mundo. A
 * diferença real entre eles é a ORDEM na lista -- o padrão vem primeiro -- e,
 * até 16/10/2026, a permissão: só o padrão exigia `#criar-filtros-padrao`.
 *
 * Os rótulos "Público" e "Padrão" não diziam isso a ninguém. A Gisele pediu
 * para juntar os dois (ponto 8, 15/10/2026); conferir antes mostrou que eles
 * não são iguais, e o que havia de fato era um portão de permissão com um
 * buraco do lado. A decisão dele: filtro usado por todos exige permissão, e o
 * "público" sai da tela.
 *
 * ## Por que o antigo continua aparecendo
 *
 * Existem filtros "públicos" criados quando aquilo não pedia nada. Esconder a
 * marca deles deixaria a equipe vendo filtro compartilhado sem nada na tela
 * explicando por quê -- e o dono sem como parar de compartilhar. Eles aparecem
 * com a marca de antigo, que é o que de fato são.
 */

export const ROTULO_EQUIPE = 'Equipe';
export const ROTULO_EQUIPE_ANTIGO = 'Equipe (antigo)';

/**
 * As marcas de compartilhamento de um filtro, na ordem em que a tela mostra.
 *
 * Um filtro pode ter os dois campos ligados, e nesse caso as duas marcas
 * aparecem: são histórias diferentes de como ele passou a ser compartilhado,
 * e é por elas que o administrador acha os que vieram da regra antiga.
 */
export const rotulosDeCompartilhamento = (filtro) => {
    const rotulos = [];

    if (filtro?.is_default) rotulos.push(ROTULO_EQUIPE);
    if (filtro?.is_public) rotulos.push(ROTULO_EQUIPE_ANTIGO);

    return rotulos;
};

/** O filtro é visível para quem não é o dono? */
export const ehCompartilhado = (filtro) => rotulosDeCompartilhamento(filtro).length > 0;

/** A permissão que governa os filtros da equipe — a mesma de compartilhar. */
export const PERMISSAO_FILTRO_DA_EQUIPE = '#criar-filtros-padrao';

/**
 * Quem pode EDITAR este filtro.
 *
 * ## Por que não é só o dono
 *
 * Era, e isso deixava o filtro COMPARTILHADO sem manutenção: quem o criou era
 * a única pessoa capaz de consertá-lo. Em 16/10/2026 isso parou o conserto de
 * quatro kanbans de equipe em produção — todos devolvendo zero, todos da mesma
 * pessoa. Quando o criador sai de férias, o filtro do time fica quebrado.
 *
 * A permissão é a MESMA de compartilhar: se tornar um filtro visível para a
 * equipe exige privilégio, manter o que já está visível exige o mesmo.
 *
 * ## O filtro PRIVADO continua só do dono
 *
 * A abertura vale para o que é da equipe. O filtro pessoal de alguém não vira
 * propriedade de quem tem a permissão.
 *
 * Espelha `FilterController::podeEditarFiltro`. Apagar continua só do dono,
 * nos dois lados: consertar é reversível, apagar o filtro que a equipe inteira
 * usa não é.
 */
export const podeEditarFiltro = (filtro, usuarioId, permissoes) => {
    if (!filtro) return false;

    // Comparado como texto: o id do filtro vem da api e o do usuário sai do
    // localStorage, e nem sempre no mesmo tipo.
    if (usuarioId != null && String(filtro.user_id) === String(usuarioId)) return true;

    if (!ehCompartilhado(filtro)) return false;

    return Array.isArray(permissoes) && permissoes.includes(PERMISSAO_FILTRO_DA_EQUIPE);
};

/**
 * Mostrar o interruptor do "público" na edição?
 *
 * Só para quem já está compartilhado por ele -- e aí serve para DESLIGAR. Não
 * há como ligar de novo: filtro novo se compartilha pelo caminho único, que
 * pede permissão.
 */
export const mostrarOInterruptorAntigo = (isPublic) => Boolean(isPublic);
